import React, { useState } from 'react';
import { Button } from '../../../components/ui/button';
import { Edit, Trash2, User as UserIcon } from 'lucide-react';
import { InputField } from '../../../components/ui/input-field';
import SelectField from '../../../components/ui/select-field';
import Toggle from '../../../components/ui/toggle';

import type { User } from '../types/user';

interface NewUserModalProps {
    open: boolean;
    onClose: () => void;
    user?: User;
}

const moduleNames = ['Dashboard', 'Agentes', 'Inbox', 'Contactos', 'Usuarios'];
const permissionKeys = ['ver', 'crear', 'editar', 'eliminar'];

const NewUserModal: React.FC<NewUserModalProps> = ({ open, onClose, user }) => {
    const [nombre, setNombre] = useState('');
    const [correo, setCorreo] = useState('');
    const [telefono, setTelefono] = useState('');
    const [rol, setRol] = useState('');
    const [estado, setEstado] = useState('');
    const [foto, setFoto] = useState('');
    const [permissions, setPermissions] = useState<{ [key: string]: [boolean, boolean, boolean, boolean] }>({});

    React.useEffect(() => {
        if (user) {
            setNombre(user.nombre || '');
            setCorreo(user.correo || '');
            setTelefono(user.telefono || '');
            setRol(user.rol === 'Administrador' ? 'admin' : user.rol === 'Usuario' ? 'user' : '');
            setEstado(user.activo ? 'activo' : 'inactivo');
            setFoto(user.foto || '');
            setPermissions(user.permissions || {});
        }
    }, [user, open]);

    if (!open) return null;
    return (
        <div className="fixed inset-0 z-50 flex items-start justify-end">
            <div className="absolute inset-0 bg-black bg-opacity-30" onClick={onClose} />
            <div className="relative bg-white rounded-l-2xl shadow-2xl w-[640px] h-full max-w-full flex flex-col border border-gray-200">
                {/* Header */}
                <div className="flex items-center bg-gray-50 rounded-tl-2xl justify-between px-6 py-3 border-b">
                    <span className="font-semibold text-base text-maxi-text-dark tracking-wide">Nuevo Usuario</span>
                    <Button
                        variant="default"
                        className="text-maxi-text-dark text-xl bg-primary rounded-full px-3 shadow"
                        onClick={onClose}
                    >
                        ×
                    </Button>
                </div>
                {/* Info Button */}
                <div className="flex items-center gap-2 px-6 py-2">
                    <Button variant="secondary" className="flex items-center gap-2 px-6 py-1 rounded-full text-xs">
                        <UserIcon className="w-4 h-4" />
                        <span>Información</span>
                    </Button>
                </div>
                {/* Subtitle */}
                <div className="px-6 pt-2 pb-1">
                    <span className="text-sm font-semibold text-maxi-text-dark">Información General</span>
                </div>
                {/* Form */}
                <form className="flex flex-col gap-6 px-6 py-2 flex-1 overflow-y-auto bg-white">
                    {/* First part: two columns */}
                    <div className="grid grid-cols-2 gap-4">
                        {/* Left: image + icons */}
                        <div className="flex flex-col items-center justify-center relative">
                            <div className="w-44 h-36 bg-gray-100 rounded-lg flex items-center justify-center border border-gray-200 relative">
                                <img src={foto || "https://randomuser.me/api/portraits/men/1.jpg"} alt={nombre || "Foto"} className="w-full h-full object-cover rounded-lg" />
                                <div className="absolute bottom-2 right-2 flex gap-1">
                                    <Button variant="secondary" className="w-6 h-6 flex items-center justify-center rounded-full min-w-0 min-h-0 p-0">
                                        <Edit className="w-4 h-4" />
                                    </Button>
                                    <Button variant="default" className="w-6 h-6 flex items-center justify-center rounded-full bg-primary min-w-0 min-h-0 p-0">
                                        <Trash2 className="w-4 h-4" />
                                    </Button>
                                </div>
                            </div>
                        </div>
                        {/* Right: nombre, correo */}
                        <div className="flex flex-col gap-2">
                            <InputField
                                label="Nombre"
                                name="nombre"
                                value={nombre}
                                onChange={e => setNombre(e.target.value)}
                                placeholder="Nombre"
                            />
                            <InputField
                                label="Correo"
                                name="correo"
                                type="email"
                                value={correo}
                                onChange={e => setCorreo(e.target.value)}
                                placeholder="Correo"
                            />
                        </div>
                    </div>
                    {/* Second part: two columns */}
                    <div className="grid grid-cols-2 gap-4">
                        {/* Left: select inputs */}
                        <div className="flex flex-col gap-2">
                            <SelectField
                                label="Rol"
                                name="rol"
                                value={rol}
                                onChange={e => setRol(e.target.value)}
                                options={[{ value: '', label: 'Rol' }, { value: 'admin', label: 'Administrador' }, { value: 'user', label: 'Usuario' }]}
                            />
                            <SelectField
                                label="Estado"
                                name="estado"
                                value={estado}
                                onChange={e => setEstado(e.target.value)}
                                options={[{ value: '', label: 'Estado' }, { value: 'activo', label: 'Activo' }, { value: 'inactivo', label: 'Inactivo' }]}
                            />
                        </div>
                        {/* Right: telefono */}
                        <div className="flex flex-col gap-2">
                            <InputField
                                label="Teléfono"
                                name="telefono"
                                value={telefono}
                                onChange={e => setTelefono(e.target.value)}
                                placeholder="Teléfono"
                            />
                        </div>
                    </div>
                    {/* Third part: matrix of booleans */}
                    <div className="mt-4">
                        <div className="text-xs font-semibold mb-2">Permisos</div>
                        <div>
                            <table className="min-w-full text-xs">
                                <thead>
                                    <tr>
                                        <th></th>
                                        <th className="px-2 py-1 text-left font-medium">Ver</th>
                                        <th className="px-2 py-1 text-left font-medium">Crear</th>
                                        <th className="px-2 py-1 text-left font-medium">Editar</th>
                                        <th className="px-2 py-1 text-left font-medium">Eliminar</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {moduleNames.map((modulo) => (
                                        <tr key={modulo}>
                                            <td className="px-2 py-1 font-semibold">{modulo}</td>
                                            {permissionKeys.map((permiso, j) => (
                                                <td key={permiso} className="px-2 py-1 text-center">
                                                    <Toggle
                                                        options={[{ label: '', value: 'true' }, { label: '', value: 'false' }]}
                                                        value={permissions[modulo]?.[j] ? 'true' : 'false'}
                                                        onChange={val => {
                                                            setPermissions(prev => ({
                                                                ...prev,
                                                                [modulo]: prev[modulo]?.map((p, idx) => idx === j ? val === 'true' : p) as [boolean, boolean, boolean, boolean]
                                                            }));
                                                        }}
                                                        className="w-10 h-5"
                                                    />
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                    {/* Fourth part: action buttons */}
                    <div className="flex justify-end gap-2 mt-4">
                        <Button variant="secondary" className="px-6 py-1 rounded-full font-semibold text-xs">Eliminar</Button>
                        <Button variant="default" className="px-6 py-1 rounded-full font-semibold bg-primary text-white text-xs">Guardar</Button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default NewUserModal;
